import { Box, Button, Divider, Pagination, Stack } from '@mui/material';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { createUserStoryTests } from 'store/ticket-service/asyncActions';
import { transformTestsResponseToState } from 'store/ticket-service/helpers';
import {
	getActiveTicket,
	getEditMode,
	getIsTestCasesGenerating,
	getIsTestCasesLoading,
	getIsTicketNavigationButtonsAvailable,
} from 'store/ticket-service/selector';
import {
	setActiveStep,
	setIsTestCasesGenerating,
	setIsTestCasesLoading,
	setIsTicketUpdate,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import { ISummaryStory, IUsecaseData } from 'store/ticket-service/types';
import { fetchUserBalance } from 'store/user-service/asyncActions';
import { getChoosedFile } from 'store/user-service/selector';
import { triggerTicketSave } from 'store/user-service/userSlice';

import { normalizeObjectKeys } from 'utils/normalizeObjectKeys';
import { toDoubleDigit } from 'utils/numberToDoubleDigitString';

import {
	getUseCaseTestCases,
	getUseCaseTestPlan,
	getUseCaseTestScenarios,
	handleNotEnoughTokensError,
} from 'core/chains/ChatGptRequests';
import { NamespacesList, TicketStages } from 'core/enums/common';

import { EditTestCasesStep } from 'components/TicketSteps/TestCasesStep/EditTestCaseStep';
import { UserStoryTests } from 'components/TicketSteps/TestCasesStep/UserStoryTests';
import {
	BackendUserStoryTest,
	ITestCase,
	IUserStoryTest,
	TestCasesStepProps,
} from 'components/TicketSteps/TestCasesStep/types';

import { ReactComponent as Stars } from 'assets/icons/stars.svg';

const linksPerPage = 1;
const useCasesTestsLoadLimit = 4;
const testCasesBatchSize = 8;

export const TestCasesStep: FC<TestCasesStepProps> = ({ ticketTitle, setTitleError }) => {
	const { t: testCasesTranslate, i18n } = useTranslation(NamespacesList['testCasesStep']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const dispatch = useAppDispatch();

	const activeTicket = useSelector(getActiveTicket);
	const editMode = useSelector(getEditMode);
	const isRequestLoading = useSelector(getIsTestCasesLoading);
	const choosedFile = useSelector(getChoosedFile);
	const isTestCasesGenerating = useSelector(getIsTestCasesGenerating);
	const isNavigationButtonAvailable = useSelector(getIsTicketNavigationButtonsAvailable);

	const [currentPage, setCurrentPage] = useState(1);
	const [userStoryTestCases, setUserStoryTestCases] = useState<Array<IUserStoryTest>>(
		activeTicket?.userStoryTests || []
	);

	// Synchronize local state with activeTicket?.userStoryTests
	useEffect(() => {
		if (activeTicket?.userStoryTests?.length) {
			setUserStoryTestCases(activeTicket.userStoryTests);
		}
	}, [activeTicket?.userStoryTests]);

	// Automatically generate Test Cases when:
	// - activeTicket?.userStoryTests is defined but empty
	// - Generation is not already in progress
	useEffect(() => {
		const shouldGenerateTestCases =
			activeTicket?.userStoryTests !== undefined &&
			activeTicket.userStoryTests.length === 0 &&
			!isTestCasesGenerating;

		if (shouldGenerateTestCases) {
			dispatch(setIsTestCasesLoading(true));
			dispatch(setIsTestCasesGenerating(true));

			handleUserStoryTestsGeneration().then(() => {
				dispatch(setIsTestCasesLoading(false));
				dispatch(setIsTicketUpdate(true));
			});
		}
	}, [activeTicket?.userStoryTests?.length, isTestCasesGenerating]);

	const currentUserStoryTestCase = useMemo(() => {
		const indexOfLastLink = currentPage * linksPerPage;
		const indexOfFirstLink = indexOfLastLink - linksPerPage;
		return userStoryTestCases.slice(indexOfFirstLink, indexOfLastLink);
	}, [currentPage, userStoryTestCases]);

	const getSplitString = (array: Array<string>) =>
		array?.reduce((accumulator: string, value: string) => {
			const splitString = value.split('');

			return splitString[splitString.length - 1] === '.'
				? accumulator + `${value} `
				: accumulator + `${value}. `;
		}, '') || '';

	const getRelatedUserStories = (stories: Array<ISummaryStory>) =>
		stories?.length
			? stories.reduce(
					(accumulator, story) =>
						accumulator + `${commonTranslate('modelsInput.story')}: ${story.data}.`,
					''
			  )
			: '';

	const getRelatedUserStoriesWithScenarios = (stories: Array<ISummaryStory>) =>
		stories?.length
			? stories.reduce(
					(accumulator, story) =>
						accumulator +
						`${commonTranslate('modelsInput.story')}: ${story.data}.` +
						(story?.summaryScenarios?.length
							? `${commonTranslate('modelsInput.scenarios')}: ` +
							  story.summaryScenarios.reduce(
									(scenariosAccumulator, scenario, index) =>
										scenariosAccumulator + `${index + 1}.${scenario.description}`,
									''
							  )
							: ''),
					''
			  )
			: '';

	const getTestCaseOrderValue = (useCaseTitle: string) => {
		const useCaseTitlesArray = activeTicket?.useCases?.map((useCase) => useCase.title);

		return useCaseTitlesArray?.findIndex((title) => title === useCaseTitle);
	};

	const formatTestCasesForBackend = (
		testCasesArray: Array<IUserStoryTest>
	): Array<BackendUserStoryTest> =>
		[
			...testCasesArray.map((item) => ({
				...item,
				testScenarios: getSplitString(item.testScenarios),
				testCases: item.testCases.map((item: ITestCase) => ({
					...item,
					testSteps: getSplitString(item.testSteps),
				})),
				order: getTestCaseOrderValue(normalizeObjectKeys(item)['usecasetitle'] || '') || 0,
			})),
		].sort((testCaseA, testCaseB) => testCaseA.order - testCaseB.order);

	const getUserStoryTestScenariosPromise = (useCase: IUsecaseData) => {
		const testsInput = {
			useCaseTitle: useCase?.title || '',
			useCaseDescription: useCase?.description || '',
			relatedUserStories: getRelatedUserStories(useCase.relatedUserStories),
			relatedUserStoriesWithScenarios: getRelatedUserStoriesWithScenarios(
				useCase.relatedUserStories
			),
			fileName: choosedFile?.name,
		};

		const testPlanPromise = getUseCaseTestPlan(testsInput);

		const testScenariosPromise = getUseCaseTestScenarios(testsInput);

		return [testPlanPromise, testScenariosPromise];
	};

	const getUserStoryTestCasesPromise = async (
		useCase: IUsecaseData,
		testScenarios: Array<string>
	): Promise<Array<ITestCase>> => {
		const testsInput = {
			useCaseTitle: useCase?.title || '',
			useCaseDescription: useCase?.description || '',
			relatedUserStoriesWithScenarios: getRelatedUserStoriesWithScenarios(
				useCase.relatedUserStories
			),
			fileName: choosedFile?.name,
		};

		const testCasesResult: Array<ITestCase> = [];

		for (let index = 0; index < testScenarios.length; index += testCasesBatchSize) {
			const currentBatch = testScenarios.slice(index, index + testCasesBatchSize);

			const testCasesResponseArray = await Promise.all(
				currentBatch.map((scenario) =>
					getUseCaseTestCases({ ...testsInput, testScenario: scenario })
				)
			);

			testCasesResult.push(...testCasesResponseArray);
		}

		return testCasesResult;
	};

	let testCasesArray: Array<IUserStoryTest> = [];

	const getUserStoryTestsPromise = (useCase: IUsecaseData, testIndex: number) =>
		Promise.all(getUserStoryTestScenariosPromise(useCase)).then((response) =>
			getUserStoryTestCasesPromise(useCase, response[1]?.testScenarios).then(
				(testCasesResponse) => {
					const updatedTestCases = testCasesResponse.map((testCase: ITestCase, testCaseIndex) => ({
						...testCase,
						testCaseId: `TC_${toDoubleDigit(testIndex + 1)}_${toDoubleDigit(testCaseIndex + 1)}`,
					}));

					const testCase = {
						testPlan: response[0]?.testPlan,
						testScenarios: response[1]?.testScenarios,
						testCases: updatedTestCases,
						usecaseId: useCase.id,
						useCaseTitle: useCase.title,
					};

					const transformedTestCase = transformTestsResponseToState(
						formatTestCasesForBackend([testCase])
					)[0];

					testCasesArray = [...testCasesArray, transformedTestCase];

					dispatch(
						updateLocalTicket({
							id: activeTicket?.id,
							userStoryTests: testCasesArray,
						})
					);

					dispatch(setIsTestCasesLoading(false));

					return transformedTestCase;
				}
			)
		);

	const saveTestCases = async (testCases: Array<BackendUserStoryTest>) => {
		try {
			const createdUserStoryTests = await dispatch(
				createUserStoryTests({
					userStoryTests: testCases,
				})
			).unwrap();

			dispatch(
				updateLocalTicket({
					id: activeTicket?.id,
					userStoryTests: createdUserStoryTests,
				})
			);
			dispatch(setIsTestCasesGenerating(false));

			setUserStoryTestCases(createdUserStoryTests);

			dispatch(setIsTicketUpdate(true));
			dispatch(triggerTicketSave());
		} catch (error) {
			console.log(error);
		}
	};

	const handleUserStoryTestsGeneration = async () =>
		new Promise((resolveMainPromise) => {
			if (activeTicket?.userStoryTests) {
				const useCases: Array<IUsecaseData> = activeTicket.useCases;
				const loadedTestCases: Array<IUserStoryTest> = [];

				let currentChunk = 0;
				let loadedChunks = 0;

				const loadTestCasesWithLimit = (limit: number) => {
					const getChunkPromise = (useCase: IUsecaseData, currentUseCaseIndex: number) =>
						new Promise((resolve) => {
							getUserStoryTestsPromise(useCase, currentUseCaseIndex)
								.then((response) => {
									loadedChunks += 1;
									loadedTestCases.push(response);

									if (loadedChunks === useCases.length) {
										saveTestCases(formatTestCasesForBackend(loadedTestCases));
										resolveMainPromise(true);
									}

									if (currentChunk < useCases.length) {
										loadTestCasesWithLimit(limit);
									}

									resolve(true);
								})
								.then(() => {
									dispatch(fetchUserBalance());
								})
								.catch((e) => handleNotEnoughTokensError({ message: e.data }));
						});

					if (currentChunk === 0) {
						currentChunk += limit;

						for (const [index, useCase] of useCases.slice(0, limit).entries())
							getChunkPromise(useCase, index);
					} else {
						if (currentChunk <= useCases.length) {
							currentChunk += 1;

							getChunkPromise(useCases[currentChunk - 1], currentChunk - 1);
						}
					}
				};

				loadTestCasesWithLimit(useCasesTestsLoadLimit);
			}
		});

	const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);
	};

	const handleNextStepNavigation = () => {
		dispatch(setActiveStep(TicketStages.notifications));

		if (TicketStages.report > (activeTicket?.currentStep || 0)) dispatch(triggerTicketSave());
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				width: '100%',
				height: '100%',
			}}
		>
			{editMode ? (
				<EditTestCasesStep
					allUserStoryTests={userStoryTestCases}
					currentUserStoryTest={currentUserStoryTestCase[0]}
					setUserStoryTests={setUserStoryTestCases}
					pageIndex={currentPage}
					ticketTitle={ticketTitle}
					setTitleError={setTitleError}
				/>
			) : (
				<Box
					className="rtl-able"
					sx={{
						overflow: 'auto',
						height: '100%',
						padding: i18n?.language === 'ar' ? '0 0 0 12px' : '0 12px 0 0',
					}}
				>
					{isRequestLoading ? (
						<Box
							sx={{
								width: '100%',
								height: 'calc(100% - 112px)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#4786ff"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						</Box>
					) : (
						<UserStoryTests userStoryTests={currentUserStoryTestCase} />
					)}
				</Box>
			)}

			{!!userStoryTestCases.length && !isRequestLoading && !editMode && (
				<>
					<Divider sx={{ background: '#E7E8EC', margin: '16px 0' }} />

					<Box
						className="not-rtl-able"
						sx={{
							width: '100%',
							display: 'flex',
							justifyContent: 'space-between',
							gap: '16px',
						}}
					>
						<Stack spacing={2}>
							<Pagination
								count={Math.ceil(userStoryTestCases.length / linksPerPage)}
								page={currentPage}
								onChange={handlePageChange}
								variant="outlined"
								shape="rounded"
							/>
						</Stack>

						{isNavigationButtonAvailable ? (
							<Button
								sx={{
									height: '44px',
									display: 'flex',
									gap: '8px',
									padding: '12px 16px',
									textTransform: 'none',
									fontWeight: '600',
								}}
								variant="contained"
								color="primary"
								disabled={isTestCasesGenerating}
								onClick={handleNextStepNavigation}
							>
								{activeTicket?.notifications?.length
									? testCasesTranslate('buttons.goToNotifications')
									: testCasesTranslate('buttons.generateNotifications')}
								<Stars style={{ marginLeft: '4px', width: '20px' }} />
							</Button>
						) : null}
					</Box>
				</>
			)}
		</Box>
	);
};
